import { useEffect, useId, type ReactNode } from "react";
import type { TooltipRenderProps } from "react-joyride";
import { Trans, useLingui } from "@lingui/macro";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Compass,
  Flag,
  Loader2,
  Pencil,
  RotateCcw,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "@app/components/ui/button";
import { cn } from "@app/lib/utils";
import { TOUR_CHAPTERS, TOUR_STOPS, type TourChapterId } from "./tour-script";

/** What a Joyride step carries in `data`: the card draws itself from the script, by id. */
export type TourCardData =
  | { kind: "intro"; chapterStarts: Record<TourChapterId, number> }
  | { kind: "stop"; stopId: string; number: number; total: number }
  | { kind: "outro" };

function isCardData(value: unknown): value is TourCardData {
  return typeof value === "object" && value !== null && "kind" in value;
}

/**
 * French high punctuation keeps its space on its line, so a card never starts a line with « : ».
 * Applied at render: the catalogues keep plain spaces, like every other string of the app.
 */
function typeset(text: ReactNode): ReactNode {
  if (typeof text !== "string") return text;
  return text
    .replace(/ ([:»])/g, "\u00a0$1")
    .replace(/« /g, "«\u00a0")
    .replace(/ ([;?!])/g, "\u202f$1");
}

/** Joyride's tooltip, redrawn in the product's own card language. */
export default function TourCard(props: TooltipRenderProps) {
  useTourKeys(props);
  const data = isCardData(props.step.data) ? props.step.data : null;
  if (data?.kind === "intro")
    return <IntroCard {...props} chapterStarts={data.chapterStarts} />;
  if (data?.kind === "stop") return <StopCard {...props} data={data} />;
  if (data?.kind === "outro") return <OutroCard {...props} />;
  return null;
}

/** Shown while a stop brings its page up — a patient page reads several endpoints. */
export function TourLoader() {
  return (
    <div
      role="status"
      className="flex items-center gap-2.5 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-slate-600 shadow-xl ring-1 ring-slate-900/10"
    >
      <Loader2
        className="h-4 w-4 animate-spin text-primary motion-reduce:animate-none"
        aria-hidden
      />
      <Trans>Préparation de l'étape…</Trans>
    </div>
  );
}

/** ← → move, Échap leaves. Joyride's own Échap closes one step, which reads as a jump. */
function useTourKeys({ controls, index }: TooltipRenderProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return;
      if (event.key === "Escape") controls.skip("button_close");
      else if (event.key === "ArrowRight") controls.next();
      else if (event.key === "ArrowLeft" && index > 0) controls.prev();
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [controls, index]);
}

function CardShell({
  tooltipProps,
  titleId,
  bodyId,
  wide = false,
  children,
}: {
  tooltipProps: TooltipRenderProps["tooltipProps"];
  titleId: string;
  bodyId: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      {...tooltipProps}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white text-left text-slate-700 shadow-2xl ring-1 ring-slate-900/10",
        "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200",
        wide
          ? "w-[min(36rem,calc(100vw-2rem))]"
          : "w-[min(25rem,calc(100vw-2rem))]",
      )}
    >
      {children}
    </div>
  );
}

function CloseButton({
  closeProps,
  light = false,
}: {
  closeProps: TooltipRenderProps["closeProps"];
  light?: boolean;
}) {
  const { t } = useLingui();
  return (
    <button
      type="button"
      {...closeProps}
      aria-label={t`Fermer la visite`}
      title={t`Fermer la visite`}
      className={cn(
        "-mr-2 -mt-1 shrink-0 rounded-lg p-1.5 transition-colors",
        light
          ? "text-white/70 hover:bg-white/15 hover:text-white"
          : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
      )}
    >
      <X className="h-4 w-4" />
    </button>
  );
}

function IntroCard({
  controls,
  primaryProps,
  skipProps,
  closeProps,
  tooltipProps,
  chapterStarts,
}: TooltipRenderProps & { chapterStarts: Record<TourChapterId, number> }) {
  const { t } = useLingui();
  const titleId = useId();
  const bodyId = useId();
  const total = TOUR_STOPS.length;

  return (
    <CardShell
      tooltipProps={tooltipProps}
      titleId={titleId}
      bodyId={bodyId}
      wide
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-primary to-primary/80 px-6 pb-6 pt-5 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 rounded-full border border-white/15"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-4 top-6 h-28 w-28 rounded-full border border-white/10"
        />
        <div className="relative flex items-start justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
            <Compass className="h-3.5 w-3.5" aria-hidden />
            <Trans>Visite guidée · {total} étapes</Trans>
          </span>
          <CloseButton closeProps={closeProps} light />
        </div>
        <h2
          id={titleId}
          className="relative mt-4 text-2xl font-bold tracking-tight"
        >
          <Trans>Comment fonctionne MAGE-X</Trans>
        </h2>
        <p
          id={bodyId}
          className="relative mt-2 text-sm leading-relaxed text-white/85"
        >
          {typeset(
            t`Des documents de vos patients à des cohortes que l'on peut comparer, sans rien ressaisir. La visite suit ce chemin ; chaque chapitre peut aussi se lancer seul.`,
          )}
        </p>
      </div>

      <ol className="space-y-2 px-6 pt-5">
        {TOUR_CHAPTERS.map((chapter, position) => (
          <li key={chapter.id}>
            <button
              type="button"
              onClick={() => controls.go(chapterStarts[chapter.id])}
              className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary tabular-nums">
                {position + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900">
                  {t(chapter.title)}
                </span>
                <span className="block text-xs text-slate-500">
                  {t(chapter.summary)}
                </span>
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-primary"
                aria-hidden
              />
            </button>
          </li>
        ))}
      </ol>

      <p className="flex items-center gap-2 px-6 pb-4 pt-3 text-xs text-slate-500">
        <ShieldCheck
          className="h-3.5 w-3.5 shrink-0 text-slate-400"
          aria-hidden
        />
        {typeset(t`Données entièrement synthétiques : aucun patient réel.`)}
      </p>

      <footer className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-3">
        <span className="hidden whitespace-nowrap text-xs text-slate-400 sm:inline">
          <Trans>← → pour naviguer · Échap pour quitter</Trans>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            {...skipProps}
            aria-label={t`Explorer seul`}
            title={t`Explorer seul`}
          >
            <Trans>Explorer seul</Trans>
          </Button>
          <Button
            size="sm"
            {...primaryProps}
            aria-label={t`Commencer la visite`}
            title={t`Commencer la visite`}
          >
            <Trans>Commencer la visite</Trans>
            <ArrowRight />
          </Button>
        </div>
      </footer>
    </CardShell>
  );
}

function StopCard({
  step,
  index,
  isLastStep,
  backProps,
  primaryProps,
  closeProps,
  tooltipProps,
  data,
}: TooltipRenderProps & { data: Extract<TourCardData, { kind: "stop" }> }) {
  const { t } = useLingui();
  const titleId = useId();
  const bodyId = useId();
  const stop = TOUR_STOPS.find((candidate) => candidate.id === data.stopId);
  const chapter = TOUR_CHAPTERS.find(
    (candidate) => candidate.id === stop?.chapter,
  );
  const Icon = stop?.icon ?? Compass;
  const n = data.number;
  const total = data.total;

  return (
    <CardShell tooltipProps={tooltipProps} titleId={titleId} bodyId={bodyId}>
      <div className="flex gap-1 px-5 pt-4" aria-hidden>
        {Array.from({ length: total }, (_, position) => (
          <span
            key={position}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors duration-300",
              position < n ? "bg-primary" : "bg-slate-200",
            )}
          />
        ))}
      </div>

      <div className="px-5 pb-4 pt-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            {chapter && (
              <p className="text-2xs font-semibold uppercase tracking-wider text-primary">
                {t(chapter.title)}
              </p>
            )}
            <h2
              id={titleId}
              className="mt-0.5 text-base font-semibold leading-snug text-slate-900"
            >
              {typeset(step.title)}
            </h2>
          </div>
          <CloseButton closeProps={closeProps} />
        </div>
        <p id={bodyId} className="mt-3 text-sm leading-relaxed text-slate-600">
          {typeset(step.content)}
        </p>
        {stop?.preview === "validation" && <ValidationPreview />}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
        <span className="text-xs text-slate-500 tabular-nums">
          <Trans>
            Étape {n} sur {total}
          </Trans>
        </span>
        <div className="flex items-center gap-2">
          {index > 0 && (
            <Button variant="ghost" size="sm" {...backProps}>
              <ArrowLeft />
              <Trans>Précédent</Trans>
            </Button>
          )}
          <Button size="sm" {...primaryProps}>
            {isLastStep ? <Trans>Terminer</Trans> : <Trans>Suivant</Trans>}
            <ArrowRight />
          </Button>
        </div>
      </footer>
    </CardShell>
  );
}

/**
 * The two gestures, drawn like the patient page draws them. The demo account reads dossiers but
 * cannot validate one, so the stop shows the gesture instead of pointing at a button that is absent.
 */
function ValidationPreview() {
  const line = (widths: [string, string]) => (
    <div className="min-w-0 flex-1 space-y-1.5">
      <span
        className={cn("block h-1.5 rounded-full bg-slate-300", widths[0])}
      />
      <span
        className={cn("block h-2 rounded-full bg-slate-400/60", widths[1])}
      />
    </div>
  );
  return (
    <div
      aria-hidden
      className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3"
    >
      <div className="flex items-center gap-2">
        {line(["w-16", "w-28"])}
        <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600">
          <Check className="h-3.5 w-3.5" />
          <Trans>Valider</Trans>
        </span>
        <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600">
          <Pencil className="h-3.5 w-3.5" />
          <Trans>Corriger</Trans>
        </span>
      </div>
      <div className="flex items-center gap-2">
        {line(["w-20", "w-24"])}
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
            <Check className="h-3.5 w-3.5" />
          </span>
          <Trans>Validée</Trans>
        </span>
      </div>
    </div>
  );
}

function OutroCard({
  controls,
  primaryProps,
  closeProps,
  tooltipProps,
}: TooltipRenderProps) {
  const { t } = useLingui();
  const titleId = useId();
  const bodyId = useId();

  return (
    <CardShell tooltipProps={tooltipProps} titleId={titleId} bodyId={bodyId}>
      <div className="flex justify-end px-5 pt-4">
        <CloseButton closeProps={closeProps} />
      </div>
      <div className="px-6 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Flag className="h-6 w-6" aria-hidden />
        </span>
        <p className="mt-4 text-2xs font-semibold uppercase tracking-wider text-primary">
          <Trans>Visite terminée</Trans>
        </p>
        <h2 id={titleId} className="mt-1 text-xl font-bold text-slate-900">
          <Trans>À vous de jouer</Trans>
        </h2>
        <p id={bodyId} className="mt-2 text-sm leading-relaxed text-slate-600">
          {typeset(
            t`Ouvrez un dossier, posez-lui une question, construisez une cohorte : tout est à portée de clic. La visite se relance à tout moment depuis le bouton « Visite guidée ».`,
          )}
        </p>
      </div>
      <ul className="mx-6 mt-5 space-y-1.5 rounded-xl bg-slate-50 p-3">
        {TOUR_CHAPTERS.map((chapter) => (
          <li
            key={chapter.id}
            className="flex items-center gap-2 text-sm text-slate-700"
          >
            <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            {t(chapter.title)}
          </li>
        ))}
      </ul>
      <footer className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
        <Button variant="ghost" size="sm" onClick={() => controls.go(0)}>
          <RotateCcw />
          <Trans>Recommencer</Trans>
        </Button>
        <Button size="sm" {...primaryProps}>
          <Trans>Terminer</Trans>
        </Button>
      </footer>
    </CardShell>
  );
}
